'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

const ENDPOINT = '/api/observability/page-view';

/**
 * Records one page view per distinct authenticated URL.
 *
 * Mounted once in the dashboard layout. The effect is keyed on pathname+search
 * so hub tab switches count too: tab state lives in ?tab= and is applied with
 * router.replace(), which updates the URL without remounting the page.
 *
 * A genuine revisit (A → B → A) re-fires and counts again, which is the
 * intended semantics for a view counter.
 */
export function PageViewReporter() {
  const pathname = usePathname();
  const search = useSearchParams()?.toString() ?? '';

  useEffect(() => {
    if (!pathname) return;

    const body = JSON.stringify({ pathname, search });
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) {
      return;
    }

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => undefined);
  }, [pathname, search]);

  return null;
}
